import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Copy, Users, TrendingUp, Share2, UserPlus, CheckCircle, Award,
  Loader2, Shield, Star, Target, Zap, ArrowRight, Gift, Crown
} from "lucide-react";

const STEP_ICONS = [Share2, UserPlus, CheckCircle, Gift];
const HOW_IT_WORKS = [
  { title: "Share Your Link", desc: "Copy your unique referral link and share it with players in your community" },
  { title: "Players Sign Up", desc: "When new players register using your link, they're automatically tracked" },
  { title: "Players Book", desc: "Earn bonus XP and credits when your recruits make their first booking" },
  { title: "Earn Rewards", desc: "Unlock badges, credits, and recognition as you grow the SetPlay community" },
];

export default function AmbassadorDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => { fetchProfile(); fetchLeaderboard(); }, []);

  const fetchProfile = async () => {
    try {
      const r = await api.get("/pilot/ambassador/me");
      setProfile(r.data);
    } catch { setProfile(null); }
    setLoading(false);
  };

  const fetchLeaderboard = async () => {
    try { const r = await api.get("/pilot/ambassador/leaderboard"); setLeaderboard(r.data.leaderboard || []); } catch {}
  };

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      await api.post("/pilot/ambassador/enroll");
      toast.success("You're now a SetPlay Ambassador!");
      await fetchProfile();
    } catch (e) { toast.error(e.response?.data?.detail || "Enrollment failed"); }
    setEnrolling(false);
  };

  const copyLink = () => {
    const link = profile?.invite_link || `${window.location.origin}/login?ref=${profile?.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied!");
  };

  const shareLink = async () => {
    const link = profile?.invite_link || `${window.location.origin}/login?ref=${profile?.referral_code}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Join SetPlay!", text: "Join me on SetPlay - book sports facilities, find coaches, and play more!", url: link }); } catch {}
    } else { copyLink(); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  // Enrollment CTA
  if (!profile) {
    const persona = user?.active_persona;
    const isCoach = persona === "COACH_SP";
    return (
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8" data-testid="ambassador-enroll-page">
        <div className="bg-slate-900 text-white p-8 md:p-12 space-y-8">
          <div className="text-center space-y-3">
            <div className="inline-flex items-center gap-2 bg-court-orange/20 text-court-orange px-4 py-1.5 rounded-full text-xs font-heading uppercase tracking-widest">
              <Crown className="w-3 h-3" />Ambassador Program
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight">
              Grow the SetPlay <br />Community
            </h1>
            <p className="text-slate-300 max-w-lg mx-auto">
              {isCoach
                ? "As a Coach, you have direct access to athletes. Recruit new players into SetPlay and earn rewards for every player who joins and books."
                : "As a Venue Owner, you interact with players daily. Recruit new players into SetPlay and earn rewards for growing the community."}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { val: "+50 XP", sub: "Per player signup" },
              { val: "+100 XP", sub: "Player's first booking" },
              { val: "₱50-200", sub: "Credits per milestone" },
              { val: "Badges", sub: "Unlock achievements" },
            ].map((r, i) => (
              <div key={i} className="bg-white/5 border border-white/10 p-4 text-center">
                <p className="font-heading text-xl font-bold text-court-orange">{r.val}</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">{r.sub}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {HOW_IT_WORKS.map((s, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <div key={i} className="flex gap-3 items-start">
                  <div className="w-8 h-8 bg-court-orange/20 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-court-orange" />
                  </div>
                  <div>
                    <p className="text-xs font-heading uppercase font-bold text-white">{s.title}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-center">
            <Button onClick={handleEnroll} disabled={enrolling} size="lg"
              className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full px-10 font-heading uppercase tracking-wide text-sm shadow-[3px_3px_0px_0px_rgba(255,255,255,0.3)]"
              data-testid="enroll-ambassador-btn">
              {enrolling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Award className="w-4 h-4 mr-2" />}
              Become an Ambassador
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard
  const milestones = profile.milestones_config || [];
  const earned = profile.milestones_earned || [];

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10 space-y-8" data-testid="ambassador-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Award className="w-7 h-7 text-court-orange" />
            <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">Ambassador Dashboard</h1>
          </div>
          <p className="text-muted-foreground">Recruit players, earn rewards, grow the community</p>
        </div>
        <Badge className="bg-court-orange text-white font-mono text-sm px-3 py-1" data-testid="amb-code-badge">{profile.referral_code}</Badge>
      </div>

      {/* Invite Link */}
      <div className="bg-white border-2 border-slate-100 p-5" data-testid="referral-link-section">
        <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground mb-2">Your Referral Link</p>
        <div className="flex gap-2">
          <Input value={profile.invite_link || `${window.location.origin}/login?ref=${profile.referral_code}`} readOnly
            className="font-mono text-xs border-2 rounded-none bg-slate-50" data-testid="referral-link-input" />
          <Button onClick={copyLink} variant="outline" className="border-2 rounded-none font-heading uppercase text-xs shrink-0" data-testid="copy-referral-btn">
            <Copy className="w-4 h-4 mr-1" />Copy
          </Button>
          <Button onClick={shareLink} className="bg-court-orange hover:bg-court-orange-hover text-white rounded-none font-heading uppercase text-xs shrink-0" data-testid="share-referral-btn">
            <Share2 className="w-4 h-4 mr-1" />Share
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">
          Share this link with players. When they sign up and book, you earn XP and credits.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { icon: UserPlus, label: "Players Recruited", value: profile.total_signups || 0, color: "text-blue-500" },
          { icon: CheckCircle, label: "Activated", value: profile.total_activations || 0, color: "text-green-500" },
          { icon: Zap, label: "Total XP", value: profile.total_xp || 0, color: "text-court-orange" },
          { icon: Star, label: "Credits Earned", value: `₱${(profile.total_credits || 0).toLocaleString()}`, color: "text-amber-500" },
          { icon: Shield, label: "Badges", value: profile.badges_earned || 0, color: "text-purple-500" },
        ].map((s, i) => (
          <div key={i} className="bg-white border-2 border-slate-100 p-5 transition-all hover:-translate-y-0.5" data-testid={`amb-stat-${i}`}>
            <s.icon className={`w-5 h-5 ${s.color} mb-2`} />
            <p className="font-heading text-2xl font-bold">{s.value}</p>
            <p className="text-[10px] font-heading uppercase tracking-[0.15em] text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Milestones */}
      <div className="bg-white border-2 border-slate-100 p-5" data-testid="milestones-section">
        <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-court-orange" />Badges & Milestones
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {milestones.map(ms => {
            const unlocked = earned.includes(ms.id);
            return (
              <div key={ms.id} className={`border-2 p-4 text-center transition-all ${unlocked ? "border-court-orange bg-orange-50" : "border-dashed border-slate-200 opacity-60"}`} data-testid={`milestone-${ms.id}`}>
                <Award className={`w-8 h-8 mx-auto mb-2 ${unlocked ? "text-court-orange" : "text-slate-300"}`} />
                <p className="font-heading text-xs font-bold uppercase">{ms.name}</p>
                <p className="text-[10px] text-muted-foreground mt-1">{ms.desc}</p>
                {unlocked && <Badge className="bg-court-orange text-white text-[8px] mt-2">UNLOCKED</Badge>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recruited Players */}
        <div className="bg-white border-2 border-slate-100 p-5" data-testid="recruited-players-section">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-court-orange" />Recruited Players
          </h2>
          {(profile.recent_referrals || []).length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-slate-200">
              <UserPlus className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="font-heading text-sm text-muted-foreground uppercase">No players recruited yet</p>
              <p className="text-xs text-muted-foreground mt-1">Share your link to start recruiting!</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[350px] overflow-y-auto">
              {profile.recent_referrals.map(r => (
                <div key={r.referral_id} className="flex items-center justify-between p-3 border border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`referral-${r.referral_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                      <Users className="w-4 h-4 text-slate-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{r.referred_user_name}</p>
                      <p className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <Badge className={r.status === "activated" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"} data-testid={`ref-status-${r.referral_id}`}>
                    {r.status === "activated" ? "Active" : "Signed Up"}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Rewards */}
        <div className="bg-white border-2 border-slate-100 p-5" data-testid="recent-rewards-section">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
            <Gift className="w-5 h-5 text-court-orange" />Recent Rewards
          </h2>
          {(profile.recent_rewards || []).length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-slate-200">
              <Gift className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="font-heading text-sm text-muted-foreground uppercase">No rewards yet</p>
              <p className="text-xs text-muted-foreground mt-1">Recruit players to start earning</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[350px] overflow-y-auto">
              {profile.recent_rewards.map(r => (
                <div key={r.reward_id} className="flex items-center justify-between p-3 border border-slate-100" data-testid={`reward-${r.reward_id}`}>
                  <div>
                    <p className="text-sm font-medium">{r.label}</p>
                    <p className="text-[10px] text-muted-foreground">{r.player_name} — {new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-heading font-bold text-court-orange">+{r.xp} XP</span>
                    {r.credits > 0 && <p className="text-[10px] text-green-600">+₱{r.credits}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <div className="bg-white border-2 border-slate-100 p-5" data-testid="amb-leaderboard">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-court-orange" />Ambassador Leaderboard
          </h2>
          <div className="space-y-2">
            {leaderboard.slice(0, 10).map((a, i) => (
              <div key={i} className="flex items-center gap-3 p-3 border border-slate-100">
                <span className={`font-heading text-lg font-bold ${i === 0 ? 'text-court-orange' : i === 1 ? 'text-slate-500' : i === 2 ? 'text-amber-700' : 'text-muted-foreground'}`}>
                  #{i + 1}
                </span>
                <div className="flex-1">
                  <p className="text-sm font-medium">{a.user_name}</p>
                  <p className="text-[10px] text-muted-foreground">{a.total_signups} recruited, {a.total_activations} activated</p>
                </div>
                <span className="font-heading font-bold text-court-orange">{a.total_xp} XP</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* How it Works */}
      <div className="bg-slate-50 border-2 border-slate-100 p-5" data-testid="how-it-works-section">
        <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4">How It Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {HOW_IT_WORKS.map((s, i) => {
            const Icon = STEP_ICONS[i];
            return (
              <div key={i} className="flex gap-3">
                <div className="w-10 h-10 bg-court-orange/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-court-orange" />
                </div>
                <div>
                  <p className="text-xs font-heading uppercase font-bold">{i + 1}. {s.title}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{s.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
